import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  type User as FirebaseUser,
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '@/services/firebase/config';
import { type User, UserRole } from '@/types/user.types';
import type { AuthContextValue } from '@/types/auth.types';
import {
  DEFAULT_AUTO_LOGOUT_TIME,
  ACTIVITY_CHECK_INTERVAL,
  COLLECTIONS,
  SETTINGS_IDS,
} from '@/utils/constants';
import { isAdmin as checkIsAdmin, isSuperAdmin as checkIsSuperAdmin } from '@/utils/roles';

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userData, setUserData] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [autoLogoutTime, setAutoLogoutTime] = useState(DEFAULT_AUTO_LOGOUT_TIME);

  // Logout handler
  const logout = useCallback(async () => {
    try {
      await signOut(auth);
      setCurrentUser(null);
      setUserData(null);
      setLastActivity(Date.now());
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }, []);

  // Track user activity
  useEffect(() => {
    const updateActivity = () => setLastActivity(Date.now());
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];

    events.forEach(event => window.addEventListener(event, updateActivity));

    return () => {
      events.forEach(event => window.removeEventListener(event, updateActivity));
    };
  }, []);

  // Auto-logout timer
  useEffect(() => {
    if (!currentUser) return;

    const checkInactivity = setInterval(() => {
      const timeSinceLastActivity = Date.now() - lastActivity;
      if (timeSinceLastActivity >= autoLogoutTime) {
        logout();
      }
    }, ACTIVITY_CHECK_INTERVAL);

    return () => clearInterval(checkInactivity);
  }, [currentUser, lastActivity, autoLogoutTime, logout]);

  // Load auto-logout settings (admin only)
  useEffect(() => {
    const loadSettings = async () => {
      if (!userData || !checkIsAdmin(userData.role)) {
        return;
      }

      try {
        const settingsDoc = await getDoc(
          doc(db, COLLECTIONS.SETTINGS, SETTINGS_IDS.AUTO_LOGOUT)
        );
        if (settingsDoc.exists()) {
          const minutes = settingsDoc.data().minutes || 30;
          setAutoLogoutTime(minutes * 60 * 1000);
        }
      } catch (error) {
        // Silently fail for non-admin users
        if (checkIsAdmin(userData.role)) {
          console.error('Error loading settings:', error);
        }
      }
    };

    loadSettings();
  }, [userData]);

  // Auth state observer
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          setCurrentUser(user);
          // Fetch user data from Firestore
          try {
            const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, user.uid));
            if (userDoc.exists()) {
              const data = userDoc.data();
              setUserData({
                uid: user.uid,
                email: data.email,
                name: data.name,
                role: data.role as UserRole,
                companyId: data.companyId,
                companyName: data.companyName,
                location: data.location,
                createdAt: data.createdAt,
                updatedAt: data.updatedAt,
              });
            }
          } catch (error) {
            console.error('Error fetching user data:', error);
          }
        } else {
          setCurrentUser(null);
          setUserData(null);
        }
      } catch (error) {
        console.error('Error in auth state change handler:', error);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  // Login handler
  const login = useCallback(async (email: string, password: string) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Fetch user data
    const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, user.uid));
    if (userDoc.exists()) {
      const data = userDoc.data();
      setUserData({
        uid: user.uid,
        email: data.email,
        name: data.name,
        role: data.role as UserRole,
        companyId: data.companyId,
        companyName: data.companyName,
        location: data.location,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
      });
    }

    setLastActivity(Date.now());
  }, []);

  // Update password handler
  const updateUserPassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      if (!currentUser) throw new Error('No user logged in');

      const credential = EmailAuthProvider.credential(
        currentUser.email!,
        currentPassword
      );

      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, newPassword);
    },
    [currentUser]
  );

  // Update user data handler
  const updateUserData = useCallback(
    async (userId: string, data: Partial<User>) => {
      try {
        await setDoc(doc(db, COLLECTIONS.USERS, userId), data, { merge: true });

        if (userId === currentUser?.uid) {
          const updatedDoc = await getDoc(doc(db, COLLECTIONS.USERS, userId));
          if (updatedDoc.exists()) {
            const docData = updatedDoc.data();
            setUserData({
              uid: userId,
              email: docData.email,
              name: docData.name,
              role: docData.role as UserRole,
              companyId: docData.companyId,
              companyName: docData.companyName,
              location: docData.location,
              createdAt: docData.createdAt,
              updatedAt: docData.updatedAt,
            });
          }
        }
      } catch (error) {
        console.error('Error updating user data:', error);
        throw error;
      }
    },
    [currentUser]
  );

  // Memoized context value
  const value = useMemo<AuthContextValue>(
    () => ({
      currentUser,
      userData,
      user: userData, // Alias for backward compatibility
      loading,
      lastActivity,
      autoLogoutTime,
      login,
      logout,
      updateUserPassword,
      updateUserData,
      setLastActivity,
      setAutoLogoutTime,
      isAuthenticated: !!currentUser && !!userData,
      isAdmin: checkIsAdmin(userData?.role ?? null),
      isSuperAdmin: checkIsSuperAdmin(userData?.role ?? null),
      userRole: userData?.role ?? null,
      companyId: userData?.companyId ?? null,
    }),
    [
      currentUser,
      userData,
      loading,
      lastActivity,
      autoLogoutTime,
      login,
      logout,
      updateUserPassword,
      updateUserData,
    ]
  );

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export default AuthContext;
